import React from 'react';

/**
 * V2Hero — editorial hero with home-studio imagery, Ken Burns, warm overlay,
 * floating coral blob + grain texture. Emits ImageObject JSON-LD when
 * `image` metadata is passed so Google indexes the hero photo.
 *
 * Two calling shapes (backwards-compatible):
 *   1. Legacy:  <V2Hero imageUrl="…" alt="…" />                   ← still works
 *   2. Meta:    <V2Hero image={HERO.homePodcastDesk} />           ← preferred
 *      (image = { src, alt, credit, creator, width, height })
 *
 * The hero image is the LCP element so we render it as an <img> with a
 * responsive Unsplash srcSet, `fetchpriority="high"` and `loading="eager"`.
 * Respects `prefers-reduced-motion` (see index.css).
 */

function unsplashAt(url, width) {
  if (!url) return url;
  try {
    const u = new URL(url);
    u.searchParams.set('w', String(width));
    if (!u.searchParams.get('auto')) u.searchParams.set('auto', 'format');
    if (!u.searchParams.get('fit')) u.searchParams.set('fit', 'crop');
    if (!u.searchParams.get('q')) u.searchParams.set('q', '75');
    return u.toString();
  } catch (e) {
    return url;
  }
}

export default function V2Hero({
  // preferred: pass the full image object from /data/heroImages.js
  image,
  // legacy props (still supported)
  imageUrl,
  alt = '',
  title,
  height = 'min-h-[560px] sm:min-h-[640px] lg:min-h-[680px]',
  children,
  overlayLeft = 0.92,
  overlayMid = 0.72,
  overlayRight = 0.42,
  showBlob = true,
  testId,
  // NEW: optional right-side collage/visual (replaces background photo when set)
  rightVisual = null,
}) {
  const finalUrl = image?.src || imageUrl;
  const finalAlt = image?.alt || alt;
  const finalTitle = image?.title || title;
  const finalCredit = image?.credit;
  const finalCreator = image?.creator;
  const finalWidth = image?.width;
  const finalHeight = image?.height;

  const overlay = `linear-gradient(90deg, rgba(246,248,251,${overlayLeft}) 0%, rgba(246,248,251,${overlayMid}) 55%, rgba(246,248,251,${overlayRight}) 100%)`;

  const srcSet = [
    `${unsplashAt(finalUrl, 640)} 640w`,
    `${unsplashAt(finalUrl, 960)} 960w`,
    `${unsplashAt(finalUrl, 1400)} 1400w`,
    `${unsplashAt(finalUrl, 1800)} 1800w`,
  ].join(', ');

  // ImageObject JSON-LD — only emit when we have real metadata to add.
  const ld = finalUrl && finalAlt && (finalCredit || finalCreator)
    ? {
        '@context': 'https://schema.org',
        '@type': 'ImageObject',
        contentUrl: unsplashAt(finalUrl, 1800),
        url: unsplashAt(finalUrl, 1800),
        description: finalAlt,
        name: finalAlt,
        width: finalWidth,
        height: finalHeight,
        license: 'https://unsplash.com/license',
        acquireLicensePage: 'https://unsplash.com/license',
        creditText: finalCredit,
        creator: finalCreator ? { '@type': 'Person', name: finalCreator } : undefined,
        copyrightNotice: finalCredit,
      }
    : null;

  return (
    <section
      className={`relative overflow-hidden bg-[#F6F8FB] v2-grain ${height}`}
      data-testid={testId}
    >
      {/* Hero image — LCP element */}
      <img
        src={unsplashAt(finalUrl, 1400)}
        srcSet={srcSet}
        sizes="100vw"
        alt={finalAlt}
        title={finalTitle}
        width={finalWidth}
        height={finalHeight}
        loading="eager"
        // eslint-disable-next-line react/no-unknown-property
        fetchpriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover v2-kenburns v2-image-grade"
      />

      {/* Warm gradient overlay for text readability */}
      <div className="absolute inset-0" style={{ background: overlay }} aria-hidden="true" />

      {/* Floating plum blob — sits behind content on the right, breathes slowly.
          Colour pulled from Jo's live 17hats calendar wash so hero decoration
          rhymes with the booking page. Hidden on very small screens so the
          copy always dominates. */}
      {showBlob && (
        <div
          className="pointer-events-none absolute -right-16 top-12 w-[320px] h-[320px] sm:w-[420px] sm:h-[420px] lg:w-[520px] lg:h-[520px] hidden sm:block v2-blob"
          aria-hidden="true"
          style={{
            background: 'radial-gradient(circle at 30% 30%, rgba(167,70,138,0.32), rgba(188,81,111,0.18) 45%, rgba(167,70,138,0) 70%)',
            filter: 'blur(6px)',
          }}
        />
      )}

      {/* Content */}
      <div className="relative max-w-[1120px] mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-24 h-full flex items-center">
        {rightVisual ? (
          <div className="w-full grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-8 lg:gap-16 items-center">
            <div>{children}</div>
            <div className="hidden lg:block">{rightVisual}</div>
          </div>
        ) : (
          <div className="w-full">{children}</div>
        )}
      </div>
      {/* Mobile-only inline visual (below the hero content) */}
      {rightVisual && (
        <div className="lg:hidden relative pb-10 pt-2 px-5 sm:px-6">
          <div className="max-w-[560px] mx-auto">{rightVisual}</div>
        </div>
      )}

      {/* Photo credit chip — bottom-right, subtle */}
      {finalCredit && (
        <div className="absolute bottom-3 right-3 z-10 text-[10px] sm:text-[11px] text-[#1E2A44]/40 bg-white/60 backdrop-blur-sm rounded px-2 py-0.5 hidden sm:block">
          {finalCredit}
        </div>
      )}

      {ld && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
        />
      )}
    </section>
  );
}
